import { useState, useEffect, useCallback } from 'react';
  const perms = detectPerms();
import {
  Plus, Pencil, Trash2, X, Layers, Search, Check, AlertCircle, Loader2, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_LABELS = {
  1: 'Sub Category',
  2: 'Child Sub Category',
  3: 'Specification',
  4: 'Sub Specification'
};

const LEVEL_COLORS = {
  1: 'bg-purple-100 text-purple-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-teal-100 text-teal-700',
  4: 'bg-amber-100 text-amber-700'
};

const initialFormData = { name: '', category_id: '', parent_id: '', level: 1, description: '', active: true };

export const SubCategories = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterLevel, setFilterLevel] = useState(0); // 0 = all
  const [filterActive, setFilterActive] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // For parent selection in form
  const [parentOptions, setParentOptions] = useState([]);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/sub-categories`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/categories`, getAuthHeaders())
      ]);
      setItems(itemsRes.data);
      setCategories(catsRes.data);
    } catch { setError('Failed to fetch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When level changes in form, fetch appropriate parent options
  const fetchParentOptions = useCallback(async (level, categoryId) => {
    if (level <= 1) { setParentOptions([]); return; }
    const parentLevel = level - 1;
    try {
      let url = `${BACKEND_URL}/api/sub-categories?level=${parentLevel}`;
      if (categoryId) url += `&category_id=${categoryId}`;
      const res = await axios.get(url, getAuthHeaders());
      setParentOptions(res.data);
    } catch { setParentOptions([]); }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleLevelChange = (level) => {
    const lvl = Number(level);
    setFormData(prev => ({ ...prev, level: lvl, parent_id: '' }));
    fetchParentOptions(lvl, formData.category_id);
  };

  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({ ...prev, category_id: categoryId, parent_id: '' }));
    if (formData.level > 1) {
      fetchParentOptions(formData.level, categoryId);
    }
  };

  const openAddModal = (level = 1, prefillName = '') => {
    setFormData({ ...initialFormData, level, name: prefillName });
    setEditingItem(null);
    setError('');
    setParentOptions([]);
    if (level > 1) fetchParentOptions(level, '');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setFormData({
      name: item.name || '',
      category_id: item.category_id || '',
      parent_id: item.parent_id || '',
      level: item.level || 1,
      description: item.description || '',
      active: item.active ?? true
    });
    setEditingItem(item);
    setError('');
    if ((item.level || 1) > 1) {
      fetchParentOptions(item.level || 1, item.category_id || '');
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) { setError('Name is required'); return; }

    // Client-side duplicate guard (case-insensitive, trimmed) — scoped by level + parent + category
    const trimmed = formData.name.trim().toLowerCase();
    const lvl = Number(formData.level);
    const dup = items.find(i =>
      i.name?.trim().toLowerCase() === trimmed &&
      (i.level || 1) === lvl &&
      ((i.parent_id || null) === (formData.parent_id || null)) &&
      ((i.category_id || null) === (formData.category_id || null)) &&
      (!editingItem || i.id !== editingItem.id)
    );
    if (dup) {
      setError('An entry with this name already exists at the same level / parent');
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (!payload.parent_id) payload.parent_id = null;
      if (formData.level > 1 && !payload.category_id) {
        // Inherit category from parent
        const parent = parentOptions.find(p => p.id === formData.parent_id);
        if (parent?.category_id) payload.category_id = parent.category_id;
      }

      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/sub-categories/${editingItem.id}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/sub-categories`, payload, getAuthHeaders());
      }
      setShowModal(false);
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/sub-categories/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchData();
    } catch { setError('Failed to delete'); }
  };

  const filtered = items.filter(i => {
    const matchSearch = i.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (i.category_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchLevel = filterLevel === 0 || (i.level || 1) === filterLevel;
    const matchActive = filterActive === '' || String(i.active) === filterActive;
    return matchSearch && matchLevel && matchActive;
  });

  return (
    <div data-testid="sub-categories-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Sub Categories</h1>
          <p className="text-slate-600 mt-1">Manage hierarchical sub categories, specifications &amp; more</p>
        </div>
        <button onClick={() => openAddModal(1)} data-testid="add-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Add Entry
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input" className="w-full pl-10 pr-10 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
            {searchQuery.trim() && (
              <button onClick={() => { openAddModal(filterLevel || 1, searchQuery.trim()); setSearchQuery(''); }}
                data-testid="search-add-btn" title="Create entry with this name"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors">
                <Plus className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {[{ val: 0, label: 'All' }, { val: 1, label: 'Sub Category' }, { val: 2, label: 'Child Sub Cat' }, { val: 3, label: 'Specification' }, { val: 4, label: 'Sub Spec' }].map(f => (
              <button key={f.val} onClick={() => setFilterLevel(f.val)} data-testid={`filter-level-${f.val}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${filterLevel === f.val ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'}`}>
                {f.label}
              </button>
            ))}
            <select value={filterActive} onChange={e => setFilterActive(e.target.value)}
              data-testid="filter-active"
              className="px-3 py-1.5 border border-slate-200 rounded-sm text-xs font-medium focus:border-orange-500 focus:outline-none bg-white min-w-[120px]">
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            {(filterLevel !== 0 || filterActive !== '') && (
              <button onClick={() => { setFilterLevel(0); setFilterActive(''); }} data-testid="clear-filters-btn"
                className="text-xs text-red-500 hover:text-red-600 font-medium px-2">Clear</button>
            )}
          </div>
        </div>
      </div>

      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Layers className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No entries found</h3>
            <p className="text-slate-500 mb-6">Add sub categories to organize your products</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-6 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Name</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Level</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Category</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Parent</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map((item) => (
              <div key={item.id} data-testid={`sub-category-row-${item.id}`}
                className="grid grid-cols-1 md:grid-cols-6 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="font-medium text-slate-900">{item.name}</div>
                <div>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded font-medium ${LEVEL_COLORS[item.level || 1]}`}>
                    {LEVEL_LABELS[item.level || 1]}
                  </span>
                </div>
                <div className="text-slate-600 text-sm">
                  {item.category_name ? (
                    <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">{item.category_name}</span>
                  ) : '-'}
                </div>
                <div className="text-slate-600 text-sm">{item.parent_name || '-'}</div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${item.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {item.active ? <><Check className="h-3 w-3" /> Active</> : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <>
                  {perms.canEdit && (
                  <button onClick={() => openEditModal(item)} data-testid={`edit-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"><Pencil className="h-4 w-4" /></button>
                  )}
                  {perms.canDelete && (
                  <button onClick={() => setDeleteConfirm(item.id)} data-testid={`delete-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"><Trash2 className="h-4 w-4" /></button>
                  )}
                  </>
                  {(item.level || 1) < 4 && (
                    <button onClick={() => openAddModal((item.level || 1) + 1)} data-testid={`add-child-btn-${item.id}`}
                      title={`Add ${LEVEL_LABELS[(item.level || 1) + 1]}`}
                      className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded transition-colors">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this entry?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingItem ? 'Edit Entry' : `Add ${LEVEL_LABELS[formData.level]}`}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" /><span>{error}</span>
                </div>
              )}
              <div className="space-y-4">
                {/* Level Selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type / Level</label>
                  <select value={formData.level} onChange={(e) => handleLevelChange(e.target.value)}
                    data-testid="input-level" disabled={!!editingItem}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none disabled:bg-slate-100">
                    <option value={1}>Sub Category</option>
                    <option value={2}>Child Sub Category</option>
                    <option value={3}>Specification</option>
                    <option value={4}>Sub Specification</option>
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                      autoComplete="off"
                      data-testid="input-name" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" required />
                    {showSuggestions && formData.name.trim() && (() => {
                      const q = formData.name.trim().toLowerCase();
                      const matches = items
                        .filter(i => i.name?.toLowerCase().includes(q))
                        .filter(i => !editingItem || i.id !== editingItem.id)
                        .slice(0, 8);
                      const isDuplicate = (i) =>
                        i.name?.toLowerCase() === q &&
                        (i.level || 1) === Number(formData.level) &&
                        ((i.parent_id || null) === (formData.parent_id || null)) &&
                        ((i.category_id || null) === (formData.category_id || null));
                      const exactDup = matches.find(isDuplicate);
                      if (matches.length === 0) return null;
                      return (
                        <div data-testid="name-suggestions"
                          className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-10 max-h-56 overflow-y-auto">
                          {exactDup && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-medium">
                              <AlertCircle className="h-3.5 w-3.5" />
                              An entry with this exact name already exists at the same level &amp; parent.
                            </div>
                          )}
                          {matches.map(i => {
                            const isExact = isDuplicate(i);
                            return (
                              <button key={i.id} type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, name: i.name }));
                                  setShowSuggestions(false);
                                }}
                                data-testid={`suggestion-${i.id}`}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center justify-between gap-2 border-b border-slate-100 last:border-b-0 ${isExact ? 'bg-amber-50/40' : ''}`}>
                                <span className="text-slate-800 truncate">{i.name}</span>
                                <span className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${LEVEL_COLORS[i.level || 1]}`}>
                                    {LEVEL_LABELS[i.level || 1]}
                                  </span>
                                  {i.parent_name && (
                                    <span className="text-[10px] text-slate-500">→ {i.parent_name}</span>
                                  )}
                                  {!i.parent_name && i.category_name && (
                                    <span className="text-[10px] text-slate-500">→ {i.category_name}</span>
                                  )}
                                  {!i.active && (
                                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Inactive</span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Category (for level 1 sub categories) */}
                {formData.level === 1 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                    <select name="category_id" value={formData.category_id} onChange={(e) => handleCategoryChange(e.target.value)}
                      data-testid="input-category" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
                      <option value="">Select Category</option>
                      {categories.filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Parent (for levels 2, 3, 4) */}
                {formData.level > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Parent ({LEVEL_LABELS[formData.level - 1]})
                    </label>
                    <select name="parent_id" value={formData.parent_id} onChange={handleInputChange}
                      data-testid="input-parent" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
                      <option value="">Select Parent</option>
                      {parentOptions.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} rows={2}
                    data-testid="input-description" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>

                {/* Active */}
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="active" name="active" checked={formData.active} onChange={handleInputChange}
                    data-testid="input-active" className="w-5 h-5 text-orange-500 border-slate-300 rounded" />
                  <label htmlFor="active" className="text-sm font-medium text-slate-700">Active</label>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
